/**
 * CSAF advisory sources — the operator's vendor-feed catalogue in the
 * dedicated "CSAF-Quellen" area of the system console.
 *
 * The CSAF sync used to pull exactly one hard-wired feed (Siemens ProductCERT,
 * from the environment). Every other vendor that publishes machine-readable
 * advisories needed a redeploy. This panel moves the list into the operator's
 * hands: add a feed URL, and the next automatic or on-demand sync — matches its
 * advisories against the portfolio and writes the affected CVEs into
 * Dependency-Track as internal vulnerabilities.
 *
 * Two things share the panel because they are the same job from the operator's
 * side, "get this vendor's advisories in":
 *
 *   - a SOURCE is a URL that is polled (ROLIE/RSS/single document) — the normal
 *     case, and what the automatic monitor walks,
 *   - an UPLOAD is one CSAF document filed under a source by hand, for
 *     advisories that arrive by e-mail or sit behind a login. It is ingested
 *     immediately, so it reports the same result shape a sync does.
 *
 * Every active source imports all CVEs, independently of portfolio relevance.
 *
 * Permissions split the same way the endpoints do: reading the catalogue comes
 * with the console (`system.admin`), every write asks for `scan.run` — a source
 * decides what the server downloads and writes into DT. Without it the controls
 * stay visible but disabled, with the missing permission in the tooltip.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Chip, type ChipTone } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import { KpiRow, KpiTile } from '../../components/KpiTile';
import ManufacturerLogo from '../../components/ManufacturerLogo';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  deleteCsafSource,
  errorMessage,
  fetchCsafSources,
  syncCsafSources,
  updateCsafSource,
  uploadCsafDocument,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DASH, countLabel, fmtDate, fmtNumber } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type {
  CsafSource,
  CsafSourceKind,
  CsafSourcePreset,
  CsafSourceStatus,
  CsafSourcesResponse,
  CsafSyncResult,
} from '../../lib/types';
import CsafSourceWizard from './CsafSourceWizard';
import CsafSourceVulnerabilities from './CsafSourceVulnerabilities';
import './csaf-sources.css';

const NO_SCAN_RUN = missingPermissionTitle('scan.run');

/** Fallback vocabulary — replaced by the server's `kinds` once loaded. */
const FALLBACK_KINDS: { kind: CsafSourceKind; label: string }[] = [
  { kind: 'rolie', label: 'ROLIE-Feed (CSAF-JSON)' },
  { kind: 'feed', label: 'RSS-/Atom-Feed' },
  { kind: 'document', label: 'Einzelnes CSAF-Dokument' },
  { kind: 'manual', label: 'Manuelle Uploads' },
  { kind: 'euvd', label: 'ENISA EUVD (EU-Datenbank)' },
];

const STATUS_TONE: Readonly<Record<CsafSourceStatus, ChipTone>> = {
  ok: 'good',
  partial: 'warn',
  error: 'alert',
};

const STATUS_LABEL: Readonly<Record<CsafSourceStatus, string>> = {
  ok: 'ok',
  partial: 'teilweise',
  error: 'Fehler',
};

/** German one-liner over a sync/upload result — the same wording either way. */
function summarise(result: CsafSyncResult): string {
  const parts = [
    `${fmtNumber(result.cves_total)} CVEs geprüft`,
    `${fmtNumber(result.cves_matched?.length)} betreffen das Portfolio`,
    `${result.created.length} CVEs neu`,
    `${result.updated.length} aktualisiert`,
  ];
  if (result.unchanged > 0) parts.push(`${fmtNumber(result.unchanged)} unverändert`);
  if (result.skipped_nvd_enriched.length > 0) {
    parts.push(`${result.skipped_nvd_enriched.length} bereits über NVD abgedeckt`);
  }
  if (result.skipped_no_document.length > 0) {
    parts.push(`${result.skipped_no_document.length} Einträge ohne CSAF-Dokument`);
  }
  if (result.reanalyzed_projects > 0) {
    parts.push(`${fmtNumber(result.reanalyzed_projects)} Dependency-Track-Projekte neu analysiert`);
  }
  return `${parts.join(', ')}.`;
}

interface LastRun {
  title: string;
  result: CsafSyncResult;
}

export function CsafSourcesCard(): ReactNode {
  const { has } = useAuth();
  const toast = useToast();
  const canWrite = has('scan.run');

  const [monitoring, setMonitoring] = useState<CsafSourcesResponse['monitoring'] | null>(null);
  const [search, setSearch] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sources, setSources] = useState<CsafSource[]>([]);
  const [kinds, setKinds] = useState(FALLBACK_KINDS);
  const [presets, setPresets] = useState<CsafSourcePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Id of the source whose row mutation is in flight. */
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<LastRun | null>(null);
  /** `undefined` closes the wizard; `null` opens an empty custom source. */
  const [wizardPreset, setWizardPreset] = useState<CsafSourcePreset | null | undefined>(undefined);

  /** One hidden file input, retargeted at whichever row asked for an upload. */
  const fileInput = useRef<HTMLInputElement | null>(null);
  const uploadTarget = useRef<string | null>(null);

  const load = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    try {
      const response = await fetchCsafSources();
      setSources(response.sources);
      setMonitoring(response.monitoring);
      if (response.kinds.length > 0) setKinds(response.kinds);
      setPresets(response.presets ?? []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => { void load(true); }, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  /** Report a sync/upload outcome: 'partial' keeps its errors on screen. */
  const report = useCallback(
    (title: string, result: CsafSyncResult): void => {
      setLastRun({ title, result });
      if (result.errors.length > 0) {
        toast.error(
          `${title}: ${countLabel(result.errors.length, 'Fehler', 'Fehler')}, Details in der Karte.`,
        );
      } else {
        toast.success(`${title}: ${result.created.length} CVEs neu, ${result.updated.length} aktualisiert.`);
      }
    },
    [toast],
  );

  function onWizardCreated(source: CsafSource): void {
    setSources((rows) => [...rows, source].sort((a, b) => a.name.localeCompare(b.name, 'de')));
    setWizardPreset(undefined);
    toast.success(`Quelle „${source.name}“ hinzugefügt und für den nächsten CSAF-Sync aktiviert.`);
  }

  async function onToggle(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      const response = await updateCsafSource(source.id, { enabled: !source.enabled });
      setSources((rows) => rows.map((row) => (row.id === source.id ? response.source : row)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      await deleteCsafSource(source.id);
      setSources((rows) => rows.filter((row) => row.id !== source.id));
      setConfirmDelete(null);
      toast.success(`Quelle „${source.name}“ entfernt.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onSync(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      const response = await syncCsafSources([source.id]);
      report(`Quelle „${source.name}“ synchronisiert`, response.result);
      await load();          // pick up the stamped last-sync state
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  function pickFile(sourceId: string): void {
    uploadTarget.current = sourceId;
    fileInput.current?.click();
  }

  async function onFileChosen(file: File): Promise<void> {
    const sourceId = uploadTarget.current;
    if (sourceId === null) return;
    const source = sources.find((row) => row.id === sourceId);
    setBusy(sourceId);
    try {
      const response = await uploadCsafDocument(sourceId, file);
      report(`Datei „${file.name}“ in „${source?.name ?? sourceId}“ übernommen`, response.result);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
      uploadTarget.current = null;
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>CSAF-Quellen</h2>
        <Chip tone={monitoring?.enabled ? 'good' : 'warn'}>
          {monitoring?.enabled ? 'Automatische Überwachung aktiv' : 'Überwachung nicht aktiv'}
        </Chip>
      </div>
      <div className="card-body">
      <KpiRow label="CSAF-Überwachung">
        <KpiTile label="Überwachte Quellen" value={sources.filter(s => s.enabled && s.kind !== 'manual').length} />
        <KpiTile label="Offizielle Quellenvorlagen" value={presets.length} />
        <KpiTile label="Prüfintervall" value={monitoring?.enabled ? `${monitoring.interval_minutes} Min.` : '—'} />
      </KpiRow>
      <p className="csaf-monitor-note">
        {monitoring?.enabled
          ? `Nächste automatische Prüfung: ${fmtDate(monitoring.next_run_at)}.`
          : 'Die automatische Überwachung ist deaktiviert. Setzen Sie CSAF_SYNC_ENABLED und starten Sie den Dienst neu.'}
        {' '}Manuelle Upload-Quellen werden nur per Datei-Upload aktualisiert.
      </p>

      <div className="csaf-source-toolbar">
        <button
          type="button"
          className="primary"
          disabled={!canWrite || busy !== null}
          title={canWrite ? 'Quelle mit Verbindungstest einrichten' : NO_SCAN_RUN}
          aria-haspopup="dialog"
          onClick={() => setWizardPreset(null)}
        >
          Neue Quelle einrichten
        </button>
        <button type="button" className="ghost small" disabled={loading} onClick={() => void load()}>
          Liste neu laden
        </button>
      </div>

      {loadError !== null ? (
        <Banner kind="error">
          {loadError}
        </Banner>
      ) : null}

      {loading ? (
        <Spinner label="Lade Quellen…" />
      ) : sources.length === 0 ? (
        <EmptyState title="Noch keine Quelle eingerichtet">
          Richten Sie über „Neue Quelle einrichten“ einen Hersteller-Feed oder eine Upload-Quelle ein.
        </EmptyState>
      ) : (
        <div className="csaf-source-grid" aria-label="Eingerichtete CSAF-Quellen">
          {sources.map(source => {
            const rowBusy = busy === source.id;
            return <article key={source.id} className={`csaf-source-card${source.enabled ? '' : ' csaf-source-paused'}`}>
              <div className="csaf-source-card-head">
                <ManufacturerLogo manufacturer={source.name} className="csaf-source-logo" />
                <div><h4>{source.name}</h4><span className="muted">{source.kind_label}</span></div>
                <Chip tone={!source.enabled ? 'idle' : source.last_status ? STATUS_TONE[source.last_status] : 'info'}>
                  {!source.enabled ? 'Pausiert' : source.last_status ? STATUS_LABEL[source.last_status] : 'Wartet auf Prüfung'}
                </Chip>
              </div>
              <div className="csaf-source-facts">
                <div><span>CVEs geprüft</span><strong>{source.last_status === null ? DASH : fmtNumber(source.last_cves)}</strong></div>
                <div><span>Davon portfolio-relevant</span><strong>{source.last_status === null ? DASH : fmtNumber(source.last_matched_cves)}</strong></div>
              </div>
              {source.last_status !== null ? <p className="csaf-source-run"><span>Zuletzt geprüft: {fmtDate(source.last_sync_at)}</span><span><strong>{fmtNumber(source.last_created ?? 0)} neue</strong> · {fmtNumber(source.last_updated ?? 0)} aktualisierte CVEs im letzten Lauf</span></p> : null}
              {source.last_status !== null && (source.last_cves == null || source.last_matched_cves == null) ? <p className="csaf-monitor-note">CVE-Anzahlen sind nach der nächsten Prüfung verfügbar.</p> : null}
              {source.last_message && source.last_status !== 'ok' ? <p className="csaf-source-warning">{source.last_message}</p> : null}
              <CsafSourceVulnerabilities source={source} />
              <div className="csaf-source-card-footer">
                <label className="check" title={!canWrite ? NO_SCAN_RUN : undefined}>
                  <input type="checkbox" checked={source.enabled} disabled={!canWrite || rowBusy} onChange={() => void onToggle(source)} />
                  {source.enabled ? 'Aktiv' : 'Pausiert'}
                </label>
                <details className="csaf-source-settings">
                  <summary>Einstellungen</summary>
                  <div className="csaf-source-settings-body">
                    {source.url ? <a className="ops-src-url" href={source.url} target="_blank" rel="noreferrer noopener">{source.url}</a> : <span className="muted">Nur Uploads</span>}
                    <p className="muted">Alle CVEs werden importiert, unabhängig von der Portfolio-Relevanz.</p>
                    <p className="muted">Ein Eintrag pro CVE.</p>
                    <div className="ops-src-actions">
                      <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => void onSync(source)}>{rowBusy ? 'Läuft…' : 'Jetzt prüfen'}</button>
                      <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => pickFile(source.id)}>Datei hochladen</button>
                      {confirmDelete === source.id ? <>
                        <button type="button" className="danger small" disabled={rowBusy} onClick={() => void onDelete(source)}>Entfernen bestätigen</button>
                        <button type="button" className="ghost small" onClick={() => setConfirmDelete(null)}>Abbrechen</button>
                      </> : <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => setConfirmDelete(source.id)}>Entfernen</button>}
                    </div>
                  </div>
                </details>
              </div>
            </article>;
          })}
        </div>
      )}

      {/* One input for every row; `uploadTarget` says which source it belongs to. */}
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';        // allow re-picking the same file
          if (file) void onFileChosen(file);
        }}
      />

      {lastRun !== null ? (
        <>
          <Banner
            kind={lastRun.result.errors.length > 0 ? 'warn' : 'ok'}
            items={lastRun.result.errors.length > 0 ? lastRun.result.errors : undefined}
          >
            <span className="ops-summary">
              {lastRun.title}: {summarise(lastRun.result)}
            </span>
          </Banner>
          <details className="ops-json">
            <summary>Rohdaten (JSON)</summary>
            <pre>{JSON.stringify(lastRun.result, null, 2)}</pre>
          </details>
        </>
      ) : null}

      {presets.length > 0 ? (
        <section className="ops-src-presets" aria-labelledby="csaf-presets-title">
          <div className="ops-src-presets-head">
            <h4 id="csaf-presets-title">Quellenkatalog</h4>
            <Chip tone="neutral">{presets.length} Quellen · Stand 10.09.2026</Chip>
          </div>
          <div className="csaf-catalog-filter">
            <input type="search" aria-label="Hersteller oder Quelle suchen" placeholder="Hersteller suchen, z. B. ABB, WAGO, Siemens …" value={search} onChange={event => setSearch(event.target.value)} />
            <label className="check"><input type="checkbox" checked={onlyAvailable} onChange={event => setOnlyAvailable(event.target.checked)} />Nur nicht eingerichtete Quellen</label>
          </div>
          {!presets.some(preset => preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) && (!onlyAvailable || !sources.some(source => source.url === preset.url))) ? <div role="status"><EmptyState compact title="Keine passenden Quellen gefunden" /></div> : null}
          <div className="ops-src-preset-grid">
            {presets.filter(preset => preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) && (!onlyAvailable || !sources.some(source => source.url === preset.url))).map((preset) => {
              const configured = sources.some(
                (source) => source.url === preset.url || source.id === preset.id,
              );
              return (
                <article className="ops-src-preset" key={preset.id}>
                  <ManufacturerLogo manufacturer={preset.name} />
                  <div className="ops-src-preset-title">
                    <strong>{preset.name}</strong>
                    <Chip tone={preset.kind === 'euvd' ? 'info' : 'neutral'}>
                      {preset.kind === 'euvd' ? 'EUVD' : 'ROLIE'}
                    </Chip>
                  </div>
                  <p>{preset.description}</p>
                  <div className="ops-src-preset-actions">
                    <a href={preset.official_url} target="_blank" rel="noreferrer noopener">
                      Offizielle Quelle
                    </a>
                    <button
                      type="button"
                      className="ghost small"
                      disabled={!canWrite || configured || busy !== null}
                      title={!canWrite ? NO_SCAN_RUN : undefined}
                      aria-haspopup="dialog"
                      onClick={() => setWizardPreset(preset)}
                    >
                      {configured ? 'Eingerichtet' : 'Einrichten'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {wizardPreset !== undefined ? (
        <CsafSourceWizard
          preset={wizardPreset}
          kinds={kinds}
          onClose={() => setWizardPreset(undefined)}
          onCreated={onWizardCreated}
        />
      ) : null}
      </div>
    </section>
  );
}

export default CsafSourcesCard;
